<script setup lang="ts">
import TileCard from '@/app/components/common/boxGrid/TileCard.vue';
import TradingLimits from 'ah-trades/src/components/limitsAndCollateral/TradingLimits.vue';
import PostedCollateral from 'ah-trades/src/components/limitsAndCollateral/PostedCollateral.vue';
import ManageCollateral from 'ah-trades/src/components/limitsAndCollateral/manageCollateral/ManageCollateral.vue';
import { computed, watch } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { ClientRiskProfileReport } from 'ah-api-gateways';
import LoadingOverlay from 'ah-common-lib/src/common/components/overlays/LoadingOverlay.vue';
import { useAuthStore } from '@/app/store/authStore';
import { useWalletsStore } from '@/app/store/walletsModule';

const props = defineProps({
  oboClientId: {
    type: String,
  },
});

const requestManager = useRequestManager().manager;

const walletStore = useWalletsStore();

const authStore = useAuthStore();

const clientId = computed(() => props.oboClientId ?? authStore.loggedInIdentity?.client?.id);

const clientRiskProfileReportData = computed<ClientRiskProfileReport | null>(() => {
  if (clientId.value) {
    return walletStore.getClientRiskProfile(clientId.value);
  }
  return null;
});

function loadClientRiskProfileReport() {
  if (!clientId?.value) {
    throw 'Cannot load client risk profile report with incomplete data';
  }

  return requestManager.newPromise(
    'loadClientRiskProfileReport',
    walletStore.loadClientRiskProfile({ force: true, clientId: clientId.value })
  );
}

watch(
  clientId,
  () => {
    if (clientId.value) {
      loadClientRiskProfileReport();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="limits-and-collateral-management">
    <LoadingOverlay
      :state="requestManager.requestStates.loadClientRiskProfileReport"
      show-retry
      @retry="loadClientRiskProfileReport"
    />
    <template
      v-if="clientRiskProfileReportData && requestManager.requestStates.loadClientRiskProfileReport !== 'error'"
    >
      <TileCard tileTitle="Trading Limits">
        <TradingLimits
          :limitsProfileData="clientRiskProfileReportData.limitsProfile"
          :isActiveMarginCall="clientRiskProfileReportData.collateralProfile.activeMarginCall"
        />
      </TileCard>
      <TileCard tileTitle="Your Posted Collateral">
        <PostedCollateral :collateralProfileData="clientRiskProfileReportData.collateralProfile" />
      </TileCard>
      <TileCard tileTitle="Manage Collateral">
        <ManageCollateral
          @reload-collateral="loadClientRiskProfileReport"
          :collateralProfileData="clientRiskProfileReportData.collateralProfile"
        />
      </TileCard>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.limits-and-collateral-management {
  font-family: $font-family-inter;

  ::v-deep .tile-card {
    margin-bottom: 3rem;
  }
}
</style>
